import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { Button } from '../components/ui/button';
import { Server, Cloud, Shield, Headphones, Settings, LineChart } from 'lucide-react';
import ConsultationModal from '../components/ConsultationModal';

const WHATSAPP_URL = 'https://wa.me/916366979916?text=Hi%20CloudXAI%2C%20I%20am%20interested%20in%20IT%20Services';

const ITServicesPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#0B0F1A]">
      <Header />
      <ConsultationModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} serviceType="IT Services" />
      
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden" style={{background: 'linear-gradient(135deg, #0B0F1A 0%, #020617 100%)'}}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{backgroundImage: 'linear-gradient(#00E5FF 1px, transparent 1px), linear-gradient(90deg, #00E5FF 1px, transparent 1px)', backgroundSize: '50px 50px'}}></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <Link to="/" className="inline-flex items-center text-[#00E5FF] hover:text-[#1DE9B6] mb-8 transition-colors">← Back to Home</Link>
          
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#00E5FF] to-[#1DE9B6] flex items-center justify-center shadow-xl shadow-[#00E5FF]/50">
              <Server className="w-8 h-8 text-[#0B0F1A]" />
            </div>
            <h1 className="text-4xl lg:text-6xl font-bold text-white font-['Space_Grotesk']">IT Services & Consulting</h1>
          </div>
          <div className="w-32 h-1 bg-gradient-to-r from-[#00E5FF] to-transparent mb-6"></div>
          <p className="text-xl text-[#9CA3AF] max-w-4xl">Enterprise IT solutions, infrastructure management & digital transformation</p>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#111827]">
        <div className="max-w-7xl mx-auto space-y-16">
          
          {/* Cloud Services */}
          <div className="bg-[#0B0F1A] border-2 border-[#00E5FF]/20 rounded-2xl p-8 hover:border-[#00E5FF]/50 transition-all duration-300">
            <div className="flex items-start space-x-6 mb-6">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00E5FF] to-[#1DE9B6] flex items-center justify-center flex-shrink-0">
                <Cloud className="w-7 h-7 text-[#0B0F1A]" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white mb-4 font-['Space_Grotesk']">Cloud Services</h2>
                <p className="text-[#9CA3AF] text-lg mb-6">We help businesses move to the cloud, optimize their cloud infrastructure, and manage workloads effectively across leading platforms.</p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-[#00E5FF] font-semibold mb-4 text-lg">Platforms:</h4>
                <div className="space-y-2">
                  <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Amazon Web Services (AWS)</span></div>
                  <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Microsoft Azure</span></div>
                  <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Google Cloud Platform (GCP)</span></div>
                </div>
              </div>
              <div>
                <h4 className="text-[#00E5FF] font-semibold mb-4 text-lg">Services:</h4>
                <div className="space-y-2">
                  <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Cloud migration & deployment</span></div>
                  <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Cost optimization</span></div>
                  <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Multi-cloud strategy</span></div>
                </div>
              </div>
            </div>
          </div>

          {/* DevOps & Site Reliability */}
          <div className="bg-[#0B0F1A] border-2 border-[#00E5FF]/20 rounded-2xl p-8 hover:border-[#00E5FF]/50 transition-all duration-300">
            <div className="flex items-start space-x-6 mb-6">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00E5FF] to-[#1DE9B6] flex items-center justify-center flex-shrink-0">
                <Settings className="w-7 h-7 text-[#0B0F1A]" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white mb-4 font-['Space_Grotesk']">DevOps & Site Reliability</h2>
                <p className="text-[#9CA3AF] text-lg mb-6">We implement DevOps practices and SRE principles to improve deployment speed, system uptime, and operational efficiency.</p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-[#00E5FF] font-semibold mb-4 text-lg">DevOps Services:</h4>
                <div className="space-y-2">
                  <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">CI/CD pipeline setup</span></div>
                  <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Infrastructure as Code (IaC)</span></div>
                  <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Container orchestration (Kubernetes)</span></div>
                </div>
              </div>
              <div>
                <h4 className="text-[#00E5FF] font-semibold mb-4 text-lg">SRE Practices:</h4>
                <div className="space-y-2">
                  <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Incident management</span></div>
                  <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Monitoring & alerting</span></div>
                  <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Reliability engineering</span></div>
                </div>
              </div>
            </div>
          </div>

          {/* Cyber Security */}
          <div className="bg-[#0B0F1A] border-2 border-[#00E5FF]/20 rounded-2xl p-8 hover:border-[#00E5FF]/50 transition-all duration-300">
            <div className="flex items-start space-x-6 mb-6">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00E5FF] to-[#1DE9B6] flex items-center justify-center flex-shrink-0">
                <Shield className="w-7 h-7 text-[#0B0F1A]" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white mb-4 font-['Space_Grotesk']">Cyber Security Services</h2>
                <p className="text-[#9CA3AF] text-lg mb-6">We help organizations protect their systems, data, and infrastructure with industry-leading security practices and compliance frameworks.</p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-[#00E5FF] font-semibold mb-4 text-lg">Security Services:</h4>
                <div className="space-y-2">
                  <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Vulnerability assessment</span></div>
                  <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Penetration testing</span></div>
                  <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Security audits</span></div>
                </div>
              </div>
              <div>
                <h4 className="text-[#00E5FF] font-semibold mb-4 text-lg">Compliance:</h4>
                <div className="space-y-2">
                  <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">SOC 2 compliance</span></div>
                  <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">ISO 27001 implementation</span></div>
                  <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">GDPR compliance</span></div>
                </div>
              </div>
            </div>
          </div>

          {/* Infrastructure Management */}
          <div className="bg-[#0B0F1A] border-2 border-[#00E5FF]/20 rounded-2xl p-8 hover:border-[#00E5FF]/50 transition-all duration-300">
            <div className="flex items-start space-x-6 mb-6">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00E5FF] to-[#1DE9B6] flex items-center justify-center flex-shrink-0">
                <Server className="w-7 h-7 text-[#0B0F1A]" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white mb-4 font-['Space_Grotesk']">Infrastructure Management</h2>
                <p className="text-[#9CA3AF] text-lg mb-6">We manage and maintain your IT infrastructure to ensure high availability, performance, and scalability.</p>
              </div>
            </div>
            <div>
              <h4 className="text-[#00E5FF] font-semibold mb-4 text-lg">Services Include:</h4>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Server administration</span></div>
                <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Network management</span></div>
                <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Backup & disaster recovery</span></div>
                <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Database administration</span></div>
              </div>
            </div>
          </div>

          {/* IT Consulting */}
          <div className="bg-[#0B0F1A] border-2 border-[#00E5FF]/20 rounded-2xl p-8 hover:border-[#00E5FF]/50 transition-all duration-300">
            <div className="flex items-start space-x-6 mb-6">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00E5FF] to-[#1DE9B6] flex items-center justify-center flex-shrink-0">
                <LineChart className="w-7 h-7 text-[#0B0F1A]" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white mb-4 font-['Space_Grotesk']">IT Consulting</h2>
                <p className="text-[#9CA3AF] text-lg mb-6">We offer strategic IT consulting to help businesses align technology with business objectives and drive digital transformation.</p>
              </div>
            </div>
            <div>
              <h4 className="text-[#00E5FF] font-semibold mb-4 text-lg">Consulting Areas:</h4>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Digital transformation roadmaps</span></div>
                <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Technology stack evaluation</span></div>
                <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">IT budgeting & planning</span></div>
                <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Vendor management</span></div>
              </div>
            </div>
          </div>

          {/* Managed IT Support */}
          <div className="bg-[#0B0F1A] border-2 border-[#00E5FF]/20 rounded-2xl p-8 hover:border-[#00E5FF]/50 transition-all duration-300">
            <div className="flex items-start space-x-6 mb-6">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00E5FF] to-[#1DE9B6] flex items-center justify-center flex-shrink-0">
                <Headphones className="w-7 h-7 text-[#0B0F1A]" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white mb-4 font-['Space_Grotesk']">Managed IT Support</h2>
                <p className="text-[#9CA3AF] text-lg mb-6">We provide 24/7 managed IT support to keep your systems running smoothly and resolve issues quickly.</p>
              </div>
            </div>
            <div>
              <h4 className="text-[#00E5FF] font-semibold mb-4 text-lg">Support Services:</h4>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Help desk support</span></div>
                <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Remote troubleshooting</span></div>
                <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Proactive monitoring</span></div>
                <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Escalation management</span></div>
              </div>
            </div>
          </div>

        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0B0F1A]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6 font-['Space_Grotesk']">Need IT Services or Consulting?</h2>
          <p className="text-xl text-[#9CA3AF] mb-8">Let our experts help you optimize your IT infrastructure and achieve your business goals</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => setIsModalOpen(true)} className="bg-[#00E5FF] hover:bg-[#1DE9B6] text-[#0B0F1A] font-semibold px-8 py-6 text-lg rounded-lg shadow-lg shadow-[#00E5FF]/40 hover:shadow-[#00E5FF]/60 transition-all duration-300">Request Consultation</Button>
            <Button onClick={() => window.open(WHATSAPP_URL, '_blank')} className="border-2 border-[#00E5FF] text-[#00E5FF] hover:bg-[#00E5FF]/10 px-8 py-6 text-lg rounded-lg transition-all duration-300 bg-transparent">Talk to Our Experts</Button>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default ITServicesPage;
